import { useCallback, useEffect, useMemo } from 'react';
import { RoiType } from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import { Chart } from '@frontend/charts';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { formatters } from '@frontend/shared';
import { useNavigate } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { featureFlags } from '../../../feature-flags';
import { trackingIds } from '../../../tracking-ids';
import { useARRoiShallowStore } from '../appointment-reminders/hooks/use-ar-roi-store';
import { useFetchARROIOOverview } from '../appointment-reminders/hooks/use-fetch-ar-roi-overview';
import { useFetchRoiTrendLine } from '../hooks/use-fetch-roi-trend-line';
import { emptyStateConfigForGauge, gaugeAppearance } from './constants';
import { renderCustomCenterLabel } from './custom-gauge-label';
import { RoiOverviewCard } from './roi-overview-card';
import { SingleRoiCard } from './single-roi-card';
import { OverviewCardProps, RoiCardBaseProps } from './types';

export const ARRoiOverviewCard = ({
  handleUpdateActiveCards,
  hasOnlyOneCard,
  timePeriod,
  ...rest
}: OverviewCardProps) => {
  const { t } = useTranslation('analytics');
  const { selectedLocationIds } = useAppScopeStore();
  const navigate = useNavigate();
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const isDemoAccount = getFeatureFlagValue(featureFlags.appointmentRemindersRoiDemoData);

  const { setFilters: setARFilters, setDoNotOverrideFiltersOnLoad: setDoNotOverrideARFiltersOnLoad } =
    useARRoiShallowStore('setFilters', 'setDoNotOverrideFiltersOnLoad');

  const filters = {
    startDate: timePeriod.value.startDate,
    endDate: timePeriod.value.endDate,
    locationIds: selectedLocationIds,
  };

  const { aggregated, isLoading } = useFetchARROIOOverview({
    filters,
    fetchNoShowRate: false,
  });

  const { isLoading: isLoadingTrendLine, trendLine } = useFetchRoiTrendLine({
    additionalRequestPayload: {
      roiType: RoiType.APPOINTMENT_REMINDERS,
    },
    filters,
    isDemoAccount,
    isEnabled: hasOnlyOneCard,
  });

  const messagesSent = aggregated?.remindersSent?.remindersCount || 0;
  const appointmentsConfirmed = aggregated?.appointmentsConfirmed?.appointmentsCount || 0;

  const redirectHandler = useCallback(() => {
    if (!filters) {
      return;
    }

    setARFilters(filters);
    setDoNotOverrideARFiltersOnLoad(true);

    navigate({
      to: '/analytics/roi/appointment-reminders',
    });
  }, [filters]);

  const commonProps: RoiCardBaseProps = useMemo(
    () => ({
      ...rest,
      backgroundConfig: {
        // Colors are not available in the theme
        gradientStart: '#F07006',
        gradientEnd: '#FCB46D',
      },
      filteredDate: timePeriod.label,
      onClick: redirectHandler,
      summaryValues: [
        {
          className: 'messages-sent',
          label: t('Messages Sent'),
          value: formatters.value(messagesSent),
        },
        {
          className: 'appointments-confirmed',
          label: t('Appointments Confirmed'),
          value: formatters.value(appointmentsConfirmed),
        },
      ],
      title: t('Appointment Reminders Conversion'),
      trackingId: trackingIds.analytics.overviewAppointmentRemindersRoi,
    }),
    [appointmentsConfirmed, messagesSent, rest]
  );

  useEffect(() => {
    handleUpdateActiveCards('appointmentReminders', !!appointmentsConfirmed);
  }, [appointmentsConfirmed]);

  if (!appointmentsConfirmed) {
    return null;
  }

  if (hasOnlyOneCard) {
    return (
      <SingleRoiCard
        {...commonProps}
        isDemoAccount={isDemoAccount}
        isLoading={isLoading || isLoadingTrendLine}
        summaryValues={[
          ...commonProps.summaryValues,
          {
            className: 'appointments-completed',
            label: t('Completed Appointments'),
            value: formatters.value(aggregated?.appointmentsCompleted?.appointmentsCompletedByReminders ?? 0),
          },
        ]}
        trendLine={trendLine}
      />
    );
  }

  return (
    <RoiOverviewCard {...commonProps} iconName='calendar' isLoading={isLoading}>
      <Chart
        colors={{ value: theme.colors.tangerine50 }}
        emptyStateConfig={emptyStateConfigForGauge}
        noBorder
        style={{ marginBottom: 0 }}
      >
        <Chart.GaugeChart
          appearance={gaugeAppearance}
          customCenterLabel={() => renderCustomCenterLabel(t('{{count}} Messages', { count: messagesSent }))}
          value={(appointmentsConfirmed / (messagesSent || 1)) * 100}
        />
      </Chart>
    </RoiOverviewCard>
  );
};
